<template>
  <div :class="classString">
    <PopupBlock v-if="!dynamicYieldOverlayIsHidden" :showOverlay="showOverlay" />
  </div>
</template>

<script>
const NEWSLETTER_EXCLUDED_CONTROLLERS = [
  'orders',
  'checkout',
  'checkout_v2',
  'landing_page',
  'newsletter',
  'quiz',
]

import { mapState, mapGetters, mapActions } from 'vuex'
import { URL_PARAMS_TO_SHOW_OVERLAYS } from '@/stores/overlay'
import popupStore from '@/stores/popup'
import { registerStores } from '@/helpers/stores'
import { getQueryFromUrl } from '@/helpers/url'
import cookieOnCloseMixin from '@/mixins/cookie-on-close'
import PopupBlock from './PopupBlock/PopupBlock.vue'

export default {
  name: 'Popup',
  components: {
    PopupBlock,
  },
  mixins: [cookieOnCloseMixin],

  data() {
    return {
      variants: On.Popup.variants,
      cookieNamePrefix: 'popup',
      dynamicYieldOverlayIsHidden: false,
      transitionHandlers: {},
    }
  },

  computed: {
    ...mapState('popup', ['isImpossibleToClose']),

    ...mapGetters({
      identifier: 'popup/identifier',
      variant: 'popup/variant',
      theme: 'popup/theme',
      cookieLifetime: 'popup/cookieLifetime',
      isVisible: 'popup/isVisible',
      closeOverlay: 'popup/closeOverlay',
      isMqSmall: 'viewport/isMqSmall',
      isSafari: 'page/isSafari',
      hasVisibleOverlays: 'overlay/hasVisibleOverlays',
    }),

    classString() {
      return `popup popup--${this.variant} popup--${this.theme}`
    },

    isShowingAnotherOverlayBasedOnUrl() {
      let isShowing = false
      for (const id in URL_PARAMS_TO_SHOW_OVERLAYS) {
        if (getQueryFromUrl(URL_PARAMS_TO_SHOW_OVERLAYS[id])) {
          isShowing = true
          break
        }
      }
      return isShowing
    },

    shouldShowNewsletterOverlay() {
      const controller = document.body.getAttribute('data-controller')
      return (
        !this.isShowingAnotherOverlayBasedOnUrl &&
        !this.hasVisibleOverlays &&
        window.location.search.indexOf('newsletter=no') < 0 &&
        NEWSLETTER_EXCLUDED_CONTROLLERS.indexOf(controller) < 0 &&
        !document.body.matches('.modal_visible')
      )
    },
  },

  watch: {
    isVisible(isVisible) {
      this.toggleDocumentOverflow(!isVisible)
    },
  },

  beforeCreate() {
    registerStores({ popup: popupStore })
  },

  methods: {
    ...mapActions({
      toggleDocumentOverflow: 'page/toggleDocumentOverflow',
    }),

    toggleIsVisible(isVisible) {
      if (isVisible && this.isImpossibleToClose) this.removePossibilityToCloseOverlay()
      this.$store.commit('popup/setState', { isVisible })
    },

    showOverlay() {
      this.showDynamicYieldOverlay()
      this.fixIncorrectPositioningOnResize()
    },

    removePossibilityToCloseOverlay() {
      this.removeCloseButton()
      this.removeEventListenersOnDyWrappers()
    },

    removeCloseButton() {
      const closeButton = document.querySelector('.dy-modal-container .dy-lb-close')
      closeButton?.parentNode.removeChild(closeButton)
    },

    removeEventListenersOnDyWrappers() {
      const popup = this.$el
      const parentClass = 'js-popup-parent-node'
      popup.parentNode.classList.add(parentClass)

      const dyModalContainer = popup.closest('.dy-modal-container')
      const elementHolder = document.createElement('div')

      // Detach popup element from the DOM
      elementHolder.appendChild(document.querySelector('.popup'))

      // Re-write the html content of the DY container in order to remove all the event listeners
      // on the DY elements (click events on close button and backdrop). The events added to
      // this.$el or its children will persist as the element was detached from the DOM.
      dyModalContainer.innerHTML = dyModalContainer.innerHTML // eslint-disable-line no-self-assign

      const popupParent = dyModalContainer.querySelector(`.${parentClass}`)
      popupParent.appendChild(elementHolder.children[0])
    },

    overrideCloseCta() {
      if (this.isImpossibleToClose) return

      const dyWrapper = document.querySelector('.dy-modal-wrapper')
      const dyClose = document.querySelector('.dy-lb-close')
      const isSafariDesktop = this.isMqSmall && this.isSafari

      if (this.closeOverlay && !isSafariDesktop) {
        const createOverrideElement = cta => {
          const el = document.createElement('div')
          el.className = 'popup-dy-close-override'
          el.addEventListener('click', e => {
            e.stopPropagation()
            this.fadeOutAndCloseOverlay()
          })
          cta.insertBefore(el, cta.firstChild)
        }
        createOverrideElement(dyWrapper)
        createOverrideElement(dyClose)
      }
      dyWrapper.addEventListener('click', e => {
        if (!e.target.closest('.dy-modal-contents')) this.toggleIsVisible(false)
      })
      dyClose.addEventListener('click', () => this.toggleIsVisible(false))
    },

    removeCloseCtaOverride() {
      document.querySelectorAll('.popup-dy-close-override').forEach(el => {
        el.parentNode.removeChild(el)
      })
    },

    fadeOutAndCloseOverlay() {
      if (this.isImpossibleToClose) return

      const dyContainer = document.querySelector('.dy-modal-container')
      const visibleClass = 'dy-modal-container--visible'
      dyContainer.classList.remove(visibleClass)

      this.listenOnceToOpacityTransitionEnd(dyContainer, 'popup-closing', () => {
        this.closeOverlay()
        this.toggleIsVisible(false)
      })
    },

    listenOnceToOpacityTransitionEnd(element, eventId, callback) {
      if (this.transitionHandlers[eventId]) return

      this.transitionHandlers[eventId] = e => {
        if (e.target !== element || e.propertyName != 'opacity') return

        element.removeEventListener('transitionend', this.transitionHandlers[eventId])
        delete this.transitionHandlers[eventId]
        callback()
      }
      element.addEventListener('transitionend', this.transitionHandlers[eventId])
    },

    showDynamicYieldOverlay() {
      const dyContainer = document.querySelector('.dy-modal-container')
      const displayClass = 'dy-modal-container--displayed'
      const visibleClass = 'dy-modal-container--visible'
      this.dynamicYieldOverlayIsHidden = false

      if (this.variant == this.variants.newsletter && !this.shouldShowNewsletterOverlay) {
        dyContainer.classList.remove(displayClass, visibleClass)
        this.dynamicYieldOverlayIsHidden = true
      } else if (dyContainer) {
        dyContainer.classList.add(displayClass)
        this.$nextTick(() => {
          const eventId = 'popup-visible'
          dyContainer.classList.add(visibleClass)
          this.toggleIsVisible(true)

          this.listenOnceToOpacityTransitionEnd(dyContainer, eventId, () => {
            this.overrideCloseCta()
          })
          // Fallback: the above 'transitionend' event is not fired on Safari
          setTimeout(() => {
            if (this.transitionHandlers[eventId]) {
              delete this.transitionHandlers[eventId]
              this.overrideCloseCta()
            }
          }, 500)
        })
      }
    },

    fixIncorrectPositioningOnResize() {
      // The hack to fade out on close generates incorrect positioning of the overlay on resize
      // (Safari only)
      this.resizeHandler = () => {
        this.removeCloseCtaOverride()
        window.removeEventListener('resize', this.resizeHandler)
      }
      window.addEventListener('resize', this.resizeHandler)
    },
  },
}
</script>

<style lang="scss">
.popup-dy-close-override {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
}
</style>

<style scoped lang="scss">
.popup {
  &--original {
    position: fixed;
    top: 0;
    left: 0;
    background: transparentize($color-black, 0.15);
    width: 100%;
    height: 100%;
    cursor: default;
    z-index: $overlay-z-index;
  }
}

.fade-popup-enter-active,
.fade-popup-leave-active {
  transition-property: opacity;
}

.fade-popup-enter-active {
  transition-duration: $transition-time;
}

.fade-popup-leave-active {
  transition-duration: $transition-time-slow;
}

.fade-popup-enter,
.fade-popup-leave-to {
  opacity: 0;
}
</style>
